import React, { useEffect, useState } from "react";
import CurrencyConverter from "./CurrencyConverter";
import AuditNoteFields, {
  AuditNoteValues,
  EMPTY_AUDIT_NOTE,
  EMPTY_AUDIT_NOTE_ERRORS,
  hasAuditNoteError,
  trimAuditNote,
  validateAuditNote,
} from "./AuditNoteFields";
import CollapsibleSection from "./CollapsibleSection";
import BillingModalShell, {
  CurrencyBadge,
  ModalLoadingState,
} from "./BillingModalShell";
import {
  CompactSelect,
  Field,
  FieldGroup,
  PrimaryButton,
  SecondaryButton,
  SectionHeading,
  SelectInput,
  SummaryLine,
  TextInput,
} from "./BillingFormControls";
import "./CustomCreditModal.css";
import { handleAddCustomPlanCredits } from "../../utils/billing";
import { useParams } from "react-router-dom";
import { notify } from "../../utils/utils";
import {
  CENTRALISED_BILLING_API,
  UNIFIED_DASHBOARD_URL,
} from "../../utils/envConfig";
import { useSelector } from "react-redux";

const normalizeCurrency = (currency?: string | null) => {
  return currency ? currency.toLowerCase() : "";
};

const getRegisteredBillingCurrency = (
  subscription?: any,
  customPlanDetails?: any,
) => {
  return (
    normalizeCurrency(customPlanDetails?.currency) ||
    normalizeCurrency(subscription?.currency) ||
    normalizeCurrency(subscription?.plan?.currency) ||
    normalizeCurrency(subscription?.plan?.metadata?.currency) ||
    "usd"
  );
};

const getCurrencyChangeBlockedMessage = (
  registeredCurrency: string,
  attemptedCurrency: string,
) => {
  return `The workspace is registered in ${registeredCurrency.toUpperCase()} currency, can't move to ${attemptedCurrency.toUpperCase()} currency`;
};

const CREDIT_RATE_BY_CURRENCY: Record<string, number> = {
  usd: 0.05,
  inr: 3.1666666,
};

const formatCalculatedAmount = (amount: number) => {
  return amount.toFixed(2).replace(/\.?0+$/, "");
};

const calculateAmountFromCredits = (
  credits: string | number,
  currency: string,
) => {
  const parsedCredits = Number(credits);

  if (!credits || Number.isNaN(parsedCredits)) {
    return "";
  }

  return formatCalculatedAmount(
    parsedCredits * (CREDIT_RATE_BY_CURRENCY[currency] || 0),
  );
};

const calculateCreditsFromAmount = (
  amount: string | number,
  currency: string,
) => {
  const parsedAmount = Number(amount);
  const rate = CREDIT_RATE_BY_CURRENCY[currency] || 0;

  if (!amount || Number.isNaN(parsedAmount) || rate === 0) {
    return "";
  }

  return formatCalculatedAmount(parsedAmount / rate);
};

const getCreditRateLabel = (currency: string) => {
  return formatCalculatedAmount(CREDIT_RATE_BY_CURRENCY[currency] || 0);
};

export default function CustomCreditModal({
  onClose,
  getCustomCreditInfoDetails,
  availableCredits,
  subscription,
  customPlanDetails,
}) {
  const params = useParams();
  const globalReducer = useSelector((state: any) => state.globalReducer);
  const requiredCurrency = getRegisteredBillingCurrency(
    subscription,
    customPlanDetails,
  );

  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [credits, setCredits] = useState("");
  const [creditsError, setCreditsError] = useState("");

  const [billingCycle, setBillingCycle] = useState("monthly");
  const [billingCycleError, setBillingCycleError] = useState("");

  const [paymentMode, setPaymentMode] = useState("offline");
  const [paymentModeError, setPaymentModeError] = useState("");

  const [currency, setCurrency] = useState("usd");
  const [currencyError, setCurrencyError] = useState("");
  const [calculatorCurrency, setCalculatorCurrency] = useState(requiredCurrency);
  const [calculatorCredits, setCalculatorCredits] = useState("");
  const [calculatorAmount, setCalculatorAmount] = useState("");

  // A note describes this change, so it is never prefilled from the plan's last
  // one — the admin has to say why *this* upgrade/downgrade is happening.
  const [auditNote, setAuditNote] =
    useState<AuditNoteValues>(EMPTY_AUDIT_NOTE);
  const [auditNoteErrors, setAuditNoteErrors] = useState(
    EMPTY_AUDIT_NOTE_ERRORS,
  );

  const [customCreditInfo, setCustomCreditInfo] = useState<any>(null);

  const [paymentLink, setPaymentLink] = useState("");
  const [loading, setLoading] = useState(false);

  const [paymentStatus, setPaymentStatus] = useState("");

  useEffect(() => {
    getCustomCreditInfo();
  }, []);

  useEffect(() => {
    setCurrency(requiredCurrency);
    setCalculatorCurrency(requiredCurrency);
    setCurrencyError("");
    setAmount(calculateAmountFromCredits(credits, requiredCurrency));
  }, [requiredCurrency]);

  const getCustomCreditInfo = async () => {
    const res = await fetch(
      `${CENTRALISED_BILLING_API}/custom/${params.workspaceId}`
    ).then((res) => res.json());

    if (res.data) {
      const loadedCredits = res.data.stripeOnlinePaymentData
        ? res.data.stripeOnlinePaymentData.credits
        : res.data.credits;
      const loadedPrice = res.data.stripeOnlinePaymentData
        ? res.data.stripeOnlinePaymentData.price
        : res.data.price;
      const loadedAmount =
        loadedPrice != null
          ? String(loadedPrice)
          : calculateAmountFromCredits(loadedCredits, requiredCurrency);

      setCustomCreditInfo(res.data);
      setAmount(loadedAmount);
      setCredits(String(loadedCredits || ""));
      setCalculatorCredits(String(loadedCredits || ""));
      setCalculatorAmount(loadedAmount);
      setBillingCycle(res.data.billingCycle);
      setPaymentMode(res.data.paymentMode);
      setPaymentLink(res.data.paymentLink);
      setCurrency(requiredCurrency);
      setPaymentStatus(res.data.onlinePaymentStatus);
    }
  };

  const handleAddCustomPlan = async () => {
    console.log(params);
    let isValid = true;

    const noteErrors = validateAuditNote(auditNote);
    setAuditNoteErrors(noteErrors);
    if (hasAuditNoteError(noteErrors)) {
      notify(noteErrors.note || noteErrors.requestedBy, "error");
      return;
    }

    if (!currency) {
      setCurrencyError("Currency is required");
      isValid = false;
    } else if (currency !== requiredCurrency) {
      const currencyRestrictionMessage = getCurrencyChangeBlockedMessage(
        requiredCurrency,
        currency,
      );
      setCurrencyError(currencyRestrictionMessage);
      notify(currencyRestrictionMessage, "error");
      isValid = false;
      return;
    } else {
      setCurrencyError("");
    }

    console.log("credits", credits);
    if (!credits) {
      setCreditsError("Credits are required");
      isValid = false;
      return;
    } else {
      setCreditsError("");
    }

    if (credits < 0) {
      setCreditsError("Invalid Credits");
      isValid = false;
      return;
    }

    if (credits && Number(credits) <= 300) {
      setCreditsError("Credits must be greater than 300");
      isValid = false;
      return;
    } else {
      setCreditsError("");
    }

    if (Number(amount) < 0 || amount < 0) {
      setAmountError("Invalid Amount");
      isValid = false;
      return;
    } else {
      setAmountError("");
    }

    if (!amount) {
      setAmountError("Invalid Amount");
      isValid = false;
      return;
    } else {
      setAmountError("");
    }

    if (paymentMode !== "offline" && (!amount || Number(amount) === 0)) {
      setAmountError("Amount is required for online payments");
      isValid = false;
      return;
    } else {
      setAmountError("");
    }
    console.log(amount);

    // if (
    //   customCreditInfo &&
    //   customCreditInfo.price === amount &&
    //   customCreditInfo.credits >= credits
    // ) {
    //   setCreditsError(
    //     `Credits must be greater than ${customCreditInfo.credits} for amount ${customCreditInfo.price}`
    //   );
    //   isValid = false;
    // } else {
    //   setCreditsError("");
    // }

    if (!billingCycle) {
      setBillingCycleError("Billing cycle is required");
      isValid = false;
      return;
    } else {
      setBillingCycleError("");
    }

    if (!paymentMode) {
      setPaymentModeError("Payment mode is required");
      isValid = false;
      return;
    } else {
      setPaymentModeError("");
    }

    if (!params.workspaceId) {
      notify("Missing workspaceId", "error");
      return;
    }

    if (isValid) {
      const trimmedNote = trimAuditNote(auditNote);
      const bodyData = {
        price: amount,
        credits,
        billingCycle,
        paymentMode,
        createdBy: globalReducer.userData._id,
        addedBy: globalReducer.userData._id,
        email: globalReducer.userData.email,
        currency,
        note: trimmedNote.note,
        requestedBy: trimmedNote.requestedBy,
      };

      if (paymentMode === "stripe") {
        bodyData[
          "redirectUrl"
        ] = `${UNIFIED_DASHBOARD_URL}/workspace/${params.workspaceId}/billing`;
      }
      setLoading(true);

      try {
        const customCreditRes = await fetch(
          `${CENTRALISED_BILLING_API}/custom/${params.workspaceId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(bodyData),
          }
        ).then((res) => res.json());

        if (customCreditRes?.error) {
          notify(customCreditRes?.error || "Error adding plan", "error");
          setLoading(false);
          onClose();
          return;
        }
        setLoading(false);
        console.log("customCreditRes", customCreditRes);

        // now register the user to custom plan.

        if (customCreditRes) {
          getCustomCreditInfoDetails();
        }
      } catch (error) {
        notify("Error adding custom plan", "error");
        setLoading(false);
        onClose();
        console.log("Error adding custom plan", error);
      }
    }
  };

  if (loading) {
    return <ModalLoadingState message="Saving custom plan..." />;
  }

  const isEdit = Boolean(customCreditInfo);
  const currencySymbol = currency === "inr" ? "₹" : "$";
  const workspaceName =
    subscription?.workspaceName || customPlanDetails?.workspaceName || "";

  const submit = () => {
    const noteErrors = validateAuditNote(auditNote);
    setAuditNoteErrors(noteErrors);
    if (hasAuditNoteError(noteErrors)) {
      notify(noteErrors.note || noteErrors.requestedBy, "error");
      return;
    }

    const c = confirm(
      `Are you sure you want to ${
        isEdit ? "edit" : "add"
      } custom credit of ${credits} credits for amount ${currency.toUpperCase()} ${
        amount || "0"
      } ${billingCycle} on "${paymentMode}" mode?`,
    );
    if (!c) {
      return;
    }

    handleAddCustomPlan();
  };

  return (
    <BillingModalShell
      title={isEdit ? "Edit custom plan" : "Add custom plan"}
      badge={<CurrencyBadge currency={currency} />}
      subtitle={
        isEdit && customCreditInfo
          ? `Currently ${customCreditInfo.credits} credits for ${currencySymbol}${customCreditInfo.price} ${customCreditInfo.billingCycle} · ${customCreditInfo.paymentMode}`
          : `Move ${
              workspaceName || "this workspace"
            } onto a negotiated credit plan.`
      }
      onClose={onClose}
      footer={
        <div className="flex flex-row flex-wrap items-center justify-between gap-3">
          <SummaryLine
            items={[
              { label: "Credits", value: credits || "—" },
              {
                label: "Amount",
                value: `${currencySymbol}${amount || "0"}`,
              },
              { label: "Cycle", value: billingCycle },
              { label: "Payment", value: paymentMode },
            ]}
          />
          <div className="flex flex-row items-center gap-2">
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton onClick={submit}>
              {isEdit ? "Save changes" : "Add custom plan"}
            </PrimaryButton>
          </div>
        </div>
      }
    >
      <section>
        <SectionHeading
          title="Plan"
          hint={`1 credit = ${currency.toUpperCase()} ${getCreditRateLabel(
            currency,
          )}`}
        />
        <FieldGroup>
          <Field
            label="Credits"
            required
            error={creditsError}
            help="Must be greater than 300."
          >
            <TextInput
              value={credits}
              onChange={(value) => {
                setCredits(value);
                setAmount(calculateAmountFromCredits(value, currency));
                setCalculatorCredits(value);
                setCalculatorAmount(
                  calculateAmountFromCredits(value, calculatorCurrency),
                );
              }}
              placeholder="10000"
              inputMode="numeric"
              error={creditsError}
            />
          </Field>

          <Field
            label="Amount"
            required
            error={amountError}
            help="Auto-filled from credits. Override if the deal differs."
          >
            <TextInput
              value={amount}
              onChange={(value) => setAmount(value)}
              placeholder="1000"
              inputMode="decimal"
              prefix={currencySymbol}
              error={amountError}
            />
          </Field>

          <Field label="Billing cycle" required error={billingCycleError}>
            <SelectInput
              value={billingCycle}
              onChange={setBillingCycle}
              error={billingCycleError}
              options={[
                { value: "monthly", label: "Monthly" },
                { value: "yearly", label: "Yearly" },
              ]}
            />
          </Field>

          <Field label="Payment mode" required error={paymentModeError}>
            <SelectInput
              value={paymentMode}
              onChange={setPaymentMode}
              error={paymentModeError}
              options={[
                { value: "offline", label: "Offline" },
                { value: "stripe", label: "Stripe" },
              ]}
            />
          </Field>

          <Field
            label="Currency"
            required
            error={currencyError}
            help={`Locked to ${requiredCurrency.toUpperCase()} for this workspace.`}
          >
            <SelectInput
              value={currency}
              onChange={(selectedCurrency) => {
                if (selectedCurrency !== requiredCurrency) {
                  notify(
                    getCurrencyChangeBlockedMessage(
                      requiredCurrency,
                      selectedCurrency,
                    ),
                    "error",
                  );
                  setCurrencyError(
                    getCurrencyChangeBlockedMessage(
                      requiredCurrency,
                      selectedCurrency,
                    ),
                  );
                  setCurrency(requiredCurrency);
                  return;
                }

                setCurrency(selectedCurrency);
                setAmount(
                  calculateAmountFromCredits(credits, selectedCurrency),
                );
                setCurrencyError("");
              }}
              options={[
                { value: "usd", label: "USD" },
                { value: "inr", label: "INR" },
              ]}
            />
          </Field>
        </FieldGroup>
      </section>

      <AuditNoteFields
        values={auditNote}
        errors={auditNoteErrors}
        onChange={(next) => {
          setAuditNote(next);
          setAuditNoteErrors(EMPTY_AUDIT_NOTE_ERRORS);
        }}
      />

      <section>
        <SectionHeading title="Reference" hint="Optional tools" />
        <div className="flex flex-col gap-3">
          <CollapsibleSection
            title="Credit calculator"
            subtitle={`1 credit = ${calculatorCurrency.toUpperCase()} ${getCreditRateLabel(
              calculatorCurrency,
            )}`}
            headerRight={
              <CompactSelect
                value={calculatorCurrency}
                ariaLabel="Calculator currency"
                onChange={(nextCurrency) => {
                  setCalculatorCurrency(nextCurrency);
                  setCalculatorAmount(
                    calculateAmountFromCredits(
                      calculatorCredits,
                      nextCurrency,
                    ),
                  );
                }}
                options={[
                  { value: "usd", label: "USD" },
                  { value: "inr", label: "INR" },
                ]}
              />
            }
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Credits">
                <TextInput
                  value={calculatorCredits}
                  onChange={(nextCredits) => {
                    if (
                      nextCredits !== "" &&
                      !/^\d*\.?\d*$/.test(nextCredits)
                    ) {
                      return;
                    }

                    setCalculatorCredits(nextCredits);
                    setCalculatorAmount(
                      calculateAmountFromCredits(
                        nextCredits,
                        calculatorCurrency,
                      ),
                    );
                  }}
                  placeholder="Enter credits"
                  inputMode="decimal"
                />
              </Field>
              <Field label="Amount">
                <TextInput
                  value={calculatorAmount}
                  onChange={(nextAmount) => {
                    if (
                      nextAmount !== "" &&
                      !/^\d*\.?\d*$/.test(nextAmount)
                    ) {
                      return;
                    }

                    setCalculatorAmount(nextAmount);
                    setCalculatorCredits(
                      calculateCreditsFromAmount(
                        nextAmount,
                        calculatorCurrency,
                      ),
                    );
                  }}
                  placeholder="Enter amount"
                  inputMode="decimal"
                  prefix={calculatorCurrency === "inr" ? "₹" : "$"}
                />
              </Field>
            </div>
            <div className="mt-3 rounded-xl bg-[#f2eff3] px-3 py-2 font-['Geist:Medium',_sans-serif] text-[13px] font-medium leading-[20px] text-[#211f26]">
              {calculatorCredits || 0} credits ={" "}
              {calculatorCurrency.toUpperCase()} {calculatorAmount || 0}
            </div>
          </CollapsibleSection>

          <CurrencyConverter
            amount={amount}
            baseCurrency={currency}
            collapsible
          />
        </div>
      </section>
    </BillingModalShell>
  );
}
